import { useMutation } from "@tanstack/react-query";
import { getSubscriptionFlowApiClient } from "@shared/apis/sidekick/api-factories";
import { toast } from "@shared/services/toast";
import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { StartSubscriptionFlowResponse } from "@shared/apis/sidekick/generated";
import { appEventEmitter } from "@shared/events/app-event-emitter";

type Props = {
  onSuccess: (data?: StartSubscriptionFlowResponse) => void;
  onError?: (error: any) => void;
  planName: string;
};

type Params = Record<string, string>;

export const useStartCredentialBasedSubscriptionFlow = (
  planId: string,
  props?: Props,
) => {
  const { onSuccess, onError, planName } = props || {};
  const api = getSubscriptionFlowApiClient();

  const mutation = useMutation({
    mutationFn: async (params: Params) => {
      const requestBody = {
        ...params,
        ...(params.email ? { email: params.email.toLowerCase() } : {}),
      };
      const response =
        await api.subscriptionFlowControllerStartSubscriptionChallengeFlow({
          planId,
          requestBody,
        });
      appEventEmitter.emit({
        eventType: "sidekick-registration-start-success",
      });
      onSuccess?.(response);
      return response;
    },
    onError: async (e) => {
      appEventEmitter.emit({
        eventType: "sidekick-registration-start-failure",
      });
      console.error("Error trying to start the subscription.", e);
      onError?.(e);
      const error = await apiResponseErrorHandler(e);
      if (error.status === 409) {
        toast.error(
          "This email is already in use. Please double-check and try again.",
        );
        return;
      }
      if (error.status === 401) {
        toast.error(
          `We were unable to verify your details with ${planName}. Please ensure that your credentials are correct.`,
          { autoClose: 8000 },
        );
        return;
      }
      toast.error(
        `Error trying to start the subscription with ${planName}. ${error.errors[0]}`,
      );
    },
  });

  return mutation;
};
